import { useEffect, useState } from "react"

import {
    AppError,
    parseAndThrowAppError,
} from "../../../../errors/errorProcessors"
import { getClientProtected } from "../../clients/clients"
import { draftApplicationMapper } from "../../mappers/draftApplication/draftApplicationMapper"
import { ILaunchDraftMapped } from "../../types/draftApplication/launchDraftTypes"
import { LAUNCH_DRAFT } from "../../urls/hybrid"

interface ILaunchDraft {
    data: ILaunchDraftMapped | undefined
    isLoading: boolean
    error: AppError | null
}

/**
 * Hook for loading a saved draft of an application.
 * Queries the draft API to collect the form data, and then passes it to
 * the application context. This allows users to continue working on a draft.
 *
 * @param {string} draftId - The ID of the draft to load
 * @returns {ILaunchDraft} - data, loading and error states.
 */
const useLaunchDraft = (draftId: string): ILaunchDraft => {
    const [data, setData] = useState<ILaunchDraftMapped | undefined>(undefined)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<AppError | null>(null)

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            setIsLoading(true)
            try {
                // Use the draft API endpoint
                const url = LAUNCH_DRAFT(draftId)
                const res = await getClientProtected(url)

                // Pass the draftId to the mapper so it's included in the returned data
                const mappedData = draftApplicationMapper.map(res, draftId)
                setData(mappedData)
            } catch (err) {
                setError(parseAndThrowAppError(err))
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [draftId])

    return {
        data,
        isLoading,
        error,
    }
}

export default useLaunchDraft
